import { createSlice } from "@reduxjs/toolkit";

// Initial state
const initialState = {
  subscriptionStatus: 'disabled', // 'disabled', 'enabled', 'pending'
  token: null,
  browserInstanceId: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  permissionStatus: 'default', // 'default', 'granted', 'denied'
  isSupported: false
};

// Helper function to get stored state from localStorage
const getStoredState = () => {
  try {
    const stored = localStorage.getItem('push_notification_state');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    return {};
  }
  return {};
};

// Helper function to persist state to localStorage
const persistState = (state) => {
  const stateToPersist = {
    subscriptionStatus: state.subscriptionStatus,
    token: state.token,
    browserInstanceId: state.browserInstanceId,
    lastUpdated: state.lastUpdated,
    permissionStatus: state.permissionStatus,
    isSupported: state.isSupported
  };
  localStorage.setItem('push_notification_state', JSON.stringify(stateToPersist));
};

// Merge initial state with stored state
const getInitialState = () => {
  const storedState = getStoredState();
  return {
    ...initialState,
    ...storedState
  };
};

const pushNotificationSlice = createSlice({
  name: 'pushNotifications',
  initialState: getInitialState(),
  reducers: {
    // Set subscription status
    setPushSubscriptionStatus: (state, action) => {
      state.subscriptionStatus = action.payload;
      state.lastUpdated = new Date().toISOString();
      state.error = null;
      persistState(state);
    },

    // Set FCM token
    setPushToken: (state, action) => {
      state.token = action.payload;
      state.lastUpdated = new Date().toISOString();
      persistState(state);
    },

    // Set browser instance ID
    setBrowserInstanceId: (state, action) => {
      state.browserInstanceId = action.payload;
      persistState(state);
    },

    // Set loading state
    setPushNotificationLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    // Set error
    setPushSubscriptionError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Clear error
    clearPushSubscriptionError: (state) => {
      state.error = null;
    },

    // Set permission status
    setPermissionStatus: (state, action) => {
      state.permissionStatus = action.payload;
      persistState(state);
    },

    // Set support status
    setPushNotificationSupport: (state, action) => {
      state.isSupported = action.payload;
      persistState(state);
    },

    // Initialize push notification state
    initializePushNotificationState: (state, action) => {
      const { isSupported, permission, browserInstanceId, token } = action.payload;

      state.isSupported = isSupported;
      state.permissionStatus = permission;
      state.browserInstanceId = browserInstanceId;

      if (token) {
        state.token = token;
      }

      // Determine subscription status based on permission and token
      if (permission === 'granted' && token) {
        state.subscriptionStatus = 'enabled';
      } else {
        state.subscriptionStatus = 'disabled';
      }

      state.lastUpdated = new Date().toISOString();
      persistState(state);
    },

    // Enable push subscription
    enablePushSubscription: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },

    // Enable push subscription success
    enablePushSubscriptionSuccess: (state, action) => {
      const { token, browserInstanceId } = action.payload;

      state.subscriptionStatus = 'enabled';
      state.token = token;
      state.browserInstanceId = browserInstanceId;
      state.isLoading = false;
      state.error = null;
      state.lastUpdated = new Date().toISOString();
      persistState(state);
    },

    // Enable push subscription failure
    enablePushSubscriptionFailure: (state, action) => {
      state.subscriptionStatus = 'disabled';
      state.isLoading = false;
      state.error = action.payload;
    },

    // Disable push subscription
    disablePushSubscription: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    // Disable push subscription success
    disablePushSubscriptionSuccess: (state) => {
      state.subscriptionStatus = 'disabled';
      state.token = null;
      state.isLoading = false;
      state.error = null;
      state.lastUpdated = new Date().toISOString();
      persistState(state);
    },

    // Disable push subscription failure
    disablePushSubscriptionFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Update subscription (for token refresh)
    updatePushSubscription: (state, action) => {
      const { token } = action.payload;

      if (state.subscriptionStatus === 'enabled') {
        state.token = token;
        state.lastUpdated = new Date().toISOString();
        persistState(state);
      }
    },

    // Reset push notification state
    resetPushNotificationState: (state) => {
      Object.assign(state, initialState);
      localStorage.removeItem('push_notification_state');
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('fcm_browser_instance_id');
    },

    // Handle foreground message
    handleForegroundMessage: (state, action) => {
      // This can be used to track message statistics or update UI state
      const message = action.payload;
    }
  }
});

// Export actions
export const {
  setPushSubscriptionStatus,
  setPushToken,
  setBrowserInstanceId,
  setPushNotificationLoading,
  setPushSubscriptionError,
  clearPushSubscriptionError,
  setPermissionStatus,
  setPushNotificationSupport,
  initializePushNotificationState,
  enablePushSubscription,
  enablePushSubscriptionSuccess,
  enablePushSubscriptionFailure,
  disablePushSubscription,
  disablePushSubscriptionSuccess,
  disablePushSubscriptionFailure,
  updatePushSubscription,
  resetPushNotificationState,
  handleForegroundMessage
} = pushNotificationSlice.actions;

// Selectors
export const selectPushNotificationState = (state) => state.pushNotifications;
export const selectIsSubscribed = (state) => state.pushNotifications.subscriptionStatus === 'enabled';
export const selectPushToken = (state) => state.pushNotifications.token;
export const selectBrowserInstanceId = (state) => state.pushNotifications.browserInstanceId;
export const selectPushNotificationError = (state) => state.pushNotifications.error;
export const selectPushNotificationLoading = (state) => state.pushNotifications.isLoading;
export const selectPermissionStatus = (state) => state.pushNotifications.permissionStatus;
export const selectIsSupported = (state) => state.pushNotifications.isSupported;

// Async thunk actions for complex operations
export const initializePushNotifications = () => async (dispatch, getState) => {
  try {
    dispatch(setPushNotificationLoading(true));

    // Import Firebase functions dynamically to avoid SSR issues
    const {
      isNotificationSupported,
      getNotificationPermission,
      getBrowserInstanceId,
      getStoredFCMToken
    } = await import('../../firebase');

    const isSupported = isNotificationSupported();
    const permission = getNotificationPermission();
    const browserInstanceId = getBrowserInstanceId();
    const storedToken = getStoredFCMToken();

    dispatch(initializePushNotificationState({
      isSupported,
      permission,
      browserInstanceId,
      token: storedToken
    }));

    dispatch(setPushNotificationLoading(false));
  } catch (error) {
    dispatch(setPushSubscriptionError(error.message));
  }
};

// Export reducer
export default pushNotificationSlice.reducer;